// src/utils/wording.js
/**
 * Product-presentation wording resolver.
 *
 * Every organisation's product-presentation profile carries a controlled
 * `wording` block (`subjectTerm` / `siteTerm` / `staffTerm`) sourced from the
 * Global pDx Feature Classification Matrix and delivered to the web app on
 * `productPresentationProfile.wording`. This module turns those term keys into
 * the localized, display-ready singular/plural labels the patient-facing app
 * uses, so a research participant sees "study" wording while a clinical patient
 * sees "clinic" wording — instead of the ambiguous "clinic / study" the UI
 * shows when no term has been resolved.
 *
 * Pure and framework-free so it can be unit-tested directly; the React binding
 * lives in `hooks/useWording.js`.
 *
 * @module utils/wording
 */

const SUBJECT_TERMS = Object.freeze(["patient", "subject"]);
const SITE_TERMS = Object.freeze(["clinic", "study"]);
const STAFF_TERMS = Object.freeze(["clinician", "administrator"]);

/**
 * Fallback terms used when a profile is missing. These match the modal values
 * across the profile catalogue — the standard medical-device wording — so an
 * unresolved profile never regresses to the doubled term.
 */
export const DEFAULT_WORDING_TERMS = Object.freeze({
  subjectTerm: "patient",
  siteTerm: "clinic",
  staffTerm: "clinician",
});

/**
 * English display fallbacks, used when a locale bundle has no `wording` entry
 * (and echoed back by the deterministic test translators). The canonical
 * values live in each locale's `wording` block.
 */
const TERM_LABEL_FALLBACKS = Object.freeze({
  patient: { one: "Patient", other: "Patients" },
  subject: { one: "Subject", other: "Subjects" },
  clinic: { one: "Clinic", other: "Clinics" },
  study: { one: "Study", other: "Studies" },
  clinician: { one: "Clinician", other: "Clinicians" },
  administrator: { one: "Administrator", other: "Administrators" },
});

function pickTerm(value, allowed, fallback) {
  const candidate = String(value || "")
    .trim()
    .toLowerCase();
  return allowed.includes(candidate) ? candidate : fallback;
}

/**
 * Normalises a resolved product-presentation profile down to its controlled
 * wording terms, applying safe defaults for anything missing or unrecognised.
 *
 * @param {{ wording?: { subjectTerm?: string, siteTerm?: string, staffTerm?: string } } | null | undefined} profile
 * @returns {{ subjectTerm: string, siteTerm: string, staffTerm: string }}
 */
export function resolveWordingTerms(profile) {
  const wording =
    profile && typeof profile === "object" ? profile.wording : null;

  return {
    subjectTerm: pickTerm(
      wording?.subjectTerm,
      SUBJECT_TERMS,
      DEFAULT_WORDING_TERMS.subjectTerm,
    ),
    siteTerm: pickTerm(
      wording?.siteTerm,
      SITE_TERMS,
      DEFAULT_WORDING_TERMS.siteTerm,
    ),
    staffTerm: pickTerm(
      wording?.staffTerm,
      STAFF_TERMS,
      DEFAULT_WORDING_TERMS.staffTerm,
    ),
  };
}

function termLabel(t, term, form) {
  return t(`wording.${term}.${form}`, TERM_LABEL_FALLBACKS[term][form]);
}

/**
 * Builds the display-ready wording labels for a set of resolved terms.
 *
 * Returns the title-case singular/plural for each role plus lowercase variants
 * for mid-sentence use, and a flat `vars` object suitable for spreading into an
 * i18next interpolation call (e.g. `t(key, fallback, wording.vars)`).
 *
 * @param {{ subjectTerm: string, siteTerm: string, staffTerm: string }} terms
 * @param {(key: string, defaultValue?: string) => string} t - An i18next translator.
 */
export function buildWording(terms, t) {
  const resolved =
    terms && typeof terms === "object" ? terms : DEFAULT_WORDING_TERMS;

  const subject = termLabel(t, resolved.subjectTerm, "one");
  const subjects = termLabel(t, resolved.subjectTerm, "other");
  const site = termLabel(t, resolved.siteTerm, "one");
  const sites = termLabel(t, resolved.siteTerm, "other");
  const staff = termLabel(t, resolved.staffTerm, "one");
  const staffs = termLabel(t, resolved.staffTerm, "other");

  const lower = (value) => String(value || "").toLocaleLowerCase();

  const vars = {
    subject,
    subjects,
    site,
    sites,
    staff,
    staffs,
    subjectLower: lower(subject),
    subjectsLower: lower(subjects),
    siteLower: lower(site),
    sitesLower: lower(sites),
    staffLower: lower(staff),
    staffsLower: lower(staffs),
  };

  return {
    terms: resolved,
    ...vars,
    vars,
  };
}

/**
 * Convenience one-shot resolver: profile → display-ready wording labels.
 *
 * @param {object | null | undefined} profile - A resolved product-presentation profile.
 * @param {(key: string, defaultValue?: string) => string} t - An i18next translator.
 */
export function resolveWording(profile, t) {
  return buildWording(resolveWordingTerms(profile), t);
}
